// @flow

import type {MockNode} from './mockNode';

// Generate a Markdoc Link with API ref URL to be used in an EntityDiagramNode
const apiRefLink = (header: string): string =>
  `/docs/api/${header
    .split(/[A-Z][a-z]+/g)
    .join('_')
    .toLowerCase()}s/object`;

// Generates a table row to be used in an EntityDiagramNode
const tableRow = (children: Array<any>): string => {
  const [name, value, hasHandle] = children;
  let valueCell = ``;
  if (hasHandle) {
    // Wrap the attribute value in a {% node %} so we can later draw an edge to it
    valueCell = `* {% node id="${hasHandle.children[0]}" %}\`${value.children[0].children[0]}\`{% /node %}`;
  } else {
    valueCell = `* \`${value.children[0]}\``;
  }
  return `---
* ${name.children[0]}
${valueCell}`;
};

// Generates the children content for the {% node %} tag
const contentMarkdoc = (content: string | Array<any>): string => {
  if (typeof content === 'string') {
    return content;
  }

  // Otherwise we have a table that we need to construct special Markdoc for
  try {
    const header = `[${content[0]}](${apiRefLink(content[0])})`;
    const table = content[1].children[1].children;
    const rows = table.map(({children}) => tableRow(children));
    const markdoc = `${header}
{% table %}
${rows.join('\n')}
{% /table %}`;
    return markdoc;
  } catch {
    // Generic handler since this code is brittle
    return `Error processing this node -- contact #docs-product`;
  }
};

// Generates {% node %} Markdoc text for a user to copy and paste
export const nodeMarkdoc = (node: MockNode): string => {
  const {attributes, children} = node;
  const {state, icon, id, x, y} = attributes;
  const idAttr = `#${id}`;
  const stateAttr = state ? `state="${state}"` : '';
  const iconAttr = icon ? `icon="${icon}"` : '';
  // Only show x/y position on a node if they are both defined and not 0 (which is our default)
  const positionAttr =
    x !== undefined && y !== undefined && (x !== 0 || y !== 0)
      ? `x=${x} y=${y}`
      : '';
  const attrs = [idAttr, stateAttr, iconAttr, positionAttr]
    .filter((a) => !!a)
    .join(' ');

  if (children) {
    return `{% node ${attrs} %}\n${contentMarkdoc(children)}\n{% /node %}`;
  }
  return `{% node ${attrs} /%}`;
};
